import { clampLabelSize, labelScale, type MapLabel } from "./mapDocument";
import {
  LANDMARK_HANDLES,
  rotatePoint,
  type LandmarkHandle,
} from "./mapLandmarkTransform";
import type { WorldPoint } from "./mapGeometry";

export function transformLabel(
  start: MapLabel,
  handle: LandmarkHandle,
  pointerStart: WorldPoint,
  pointer: WorldPoint,
  dimensions: { width: number; height: number },
  snap: boolean,
): MapLabel {
  const rotation = start.rotation ?? 0;
  if (handle === "rotate") {
    const angle = (point: WorldPoint): number =>
      Math.atan2(point.y - start.y, point.x - start.x);
    let degrees =
      rotation + ((angle(pointer) - angle(pointerStart)) * 180) / Math.PI;
    if (snap) degrees = Math.round(degrees / 15) * 15;
    return {
      ...start,
      rotation: ((((degrees + 180) % 360) + 360) % 360) - 180,
    };
  }
  const direction = LANDMARK_HANDLES.find((item) => item.id === handle)!;
  const offset = rotatePoint(
    {
      x: (direction.x * dimensions.width) / 2,
      y: (direction.y * dimensions.height) / 2,
    },
    rotation,
  );
  const anchor = { x: start.x - offset.x, y: start.y - offset.y };
  const vector = { x: offset.x * 2, y: offset.y * 2 };
  const delta = {
    x: pointer.x - pointerStart.x,
    y: pointer.y - pointerStart.y,
  };
  const denominator = vector.x ** 2 + vector.y ** 2;
  const ratio =
    denominator > 0
      ? 1 + (delta.x * vector.x + delta.y * vector.y) / denominator
      : 1;
  const size = clampLabelSize(labelScale(start) * ratio);
  const scale = size / labelScale(start);
  return {
    ...start,
    size,
    x: anchor.x + offset.x * scale,
    y: anchor.y + offset.y * scale,
  };
}
